/**
 * Страница по ссылке из письма.
 *
 * Открывают её нередко в другом браузере, где сессии нет, поэтому метод
 * подтверждения публичный, а страница не требует входа. Если пользователь
 * всё-таки вошёл, после успеха обновляем сессию: признак подтверждения живёт
 * в access-токене, и без обновления интерфейс продолжал бы считать адрес
 * неподтверждённым до истечения токена.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useVerifyEmail } from "../api/queries";
import { Spinner } from "../components/ui";
import { useAuthStore } from "../stores/authStore";

type State = "checking" | "success" | "already" | "error";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const { t } = useTranslation();
  const verify = useVerifyEmail();
  const restore = useAuthStore((state) => state.restore);
  const status = useAuthStore((state) => state.status);

  const [state, setState] = useState<State>(token ? "checking" : "error");
  const [message, setMessage] = useState<string | null>(
    token ? null : t("verifyPage.noToken"),
  );

  // Ссылку открывают один раз, но в разработке эффекты запускаются дважды,
  // а токен одноразовый: второй запрос получил бы ошибку.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    verify.mutate(token, {
      onSuccess: async (response) => {
        setState(response.alreadyVerified ? "already" : "success");
        // Токен в памяти вкладки ещё говорит, что адрес не подтверждён.
        if (status === "authenticated") await restore();
      },
      onError: (error) => {
        setState("error");
        setMessage(
          error instanceof ApiError
            ? error.message
            : t("verifyPage.failed"),
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="mx-auto max-w-md py-10">
      <div className="panel p-7 text-center">
        {state === "checking" && <Spinner label={t("verifyPage.checking")} />}

        {(state === "success" || state === "already") && (
          <>
            <Mark tone="ok" />
            <h1 className="mt-4 font-sans text-3xl text-ink-50">
              {state === "success" ? t("verifyPage.done") : t("verifyPage.already")}
            </h1>
            <p className="mt-2 text-sm text-ink-400">
              {state === "success"
                ? t("verifyPage.doneHint")
                : t("verifyPage.alreadyHint")}
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Link to="/quests" className="btn-primary">
                {t("landing.play")}
              </Link>
              {status !== "authenticated" && (
                <Link to="/login" className="btn-secondary">
                  {t("nav.login")}
                </Link>
              )}
            </div>
          </>
        )}

        {state === "error" && (
          <>
            <Mark tone="error" />
            <h1 className="mt-4 font-sans text-3xl text-ink-50">{t("verifyPage.brokenTitle")}</h1>
            <p className="mt-2 text-sm text-rust">{message}</p>
            <p className="mt-3 text-sm text-ink-400">
              {t("verifyPage.brokenHint")}
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Link to="/login" className="btn-primary">
                {t("nav.login")}
              </Link>
              <Link to="/" className="btn-ghost">
                {t("notFound.home")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Mark({ tone }: { tone: "ok" | "error" }) {
  const isOk = tone === "ok";

  return (
    <div
      className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full border ${
        isOk ? "border-sage/40 bg-sage/10 text-sage" : "border-rust/40 bg-rust/10 text-rust"
      }`}
      aria-hidden
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        {isOk ? (
          <path d="M4 12.5l5 5L20 6.5" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <>
            <path d="M12 7v6" strokeLinecap="round" />
            <circle cx="12" cy="17" r="1.2" fill="currentColor" stroke="none" />
          </>
        )}
      </svg>
    </div>
  );
}
